<template>
  <div ref="locationMap" class="location-map" :style="{ height: mapHeight }" />
</template>

<script>
import { mapState } from 'vuex'
import { googleMapInit, defaultCenter } from '../../services/googleMapService'
import { isEmpty, toCapitalCase } from '../../utils/generalUtils'
import colors from '../../scss/_colors.scss'

export default {
  props: {
    mapZoom: {
      type: Number,
      default: 8
    },
    mapHeight: {
      type: String,
      default: '350px'
    },
    route: {
      type: Object,
      default: () => {}
    },
    mobileRoute: {
      type: Boolean,
      default: false
    },
    mapMarkers: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      latLng: null,
      locationMap: null,
      directionsDisplay: null,
      markerArray: [],
      customMarkerArray: [],
      customMarkerIcons: null
    }
  },

  computed: {
    ...mapState('agent', ['routeEditMode']),
    ...mapState('client', ['clientFavorites']),
    ...mapState('common', ['isMobile'])
  },

  watch: {
    route: {
      handler: async function(value) {
        await this.initGoogleMap(value)
      },
      deep: true
    }
  },

  async mounted() {
    await this.initGoogleMap()
  },

  methods: {
    async loadGoogleService() {
      try {
        const google = await googleMapInit()

        this.customMarkerIcons = {
          start: {
            url: '/img/dark-gray-solid-marker.png',
            labelOrigin: new google.maps.Point(16, 20)
          },
          end: {
            url: '/img/dark-gray-solid-marker.png',
            labelOrigin: new google.maps.Point(16, 20)
          },
          default: {
            url: '/img/brand-blue-solid-marker.png',
            labelOrigin: new google.maps.Point(16, 20)
          }
        }
        this.directionsDisplay = new google.maps.DirectionsRenderer()

        this.directionsDisplay.setOptions({
          suppressMarkers: true,
          polylineOptions: { strokeColor: colors.hwayzShadow, strokeWeight: 5 }
        })

        if (!isEmpty(this.route) && !isEmpty(this.route.markers)) {
          this.latLng = {
            lat: this.route.markers[0].position.lat,
            lng: this.route.markers[0].position.lng
          }
        } else if (!isEmpty(this.mapMarkers)) {
          this.latLng = {
            lat: this.mapMarkers[0].position.lat,
            lng: this.mapMarkers[0].position.lng
          }
        } else {
          this.latLng = defaultCenter
        }

        this.mapOptions = {
          center: this.latLng,
          zoom: this.mapZoom,
          maxZoom: 16
        }

        this.locationMap = new google.maps.Map(
          this.$refs.locationMap,
          this.mapOptions
        )
      } catch (error) {
        console.error(error)
      }
    },

    async initGoogleMap(routeData = { ...this.route }) {
      await this.loadGoogleService()
      if (!isEmpty(routeData)) {
        if (
          routeData.returnedRoute &&
          routeData.calculatedArray &&
          routeData.calculatedArray.length > 1
        ) {
          this.createDirectionMap(routeData.returnedRoute)
        } else {
          this.showMarkers(routeData.markers)
        }
      } else if (!isEmpty(this.mapMarkers)) {
        this.showMarkers(this.mapMarkers)
      }
    },

    createDirectionMap(route) {
      this.clearMarkers()
      this.directionsDisplay.setMap(null)
      this.directionsDisplay.setMap(this.locationMap)
      this.directionsDisplay.setDirections(route)

      let markerLabel = 'A'
      let zIndex = 100

      for (let i = 0; i < this.route.calculatedArray.length; i++) {
        const routeStop = this.route.calculatedArray[i]
        this.createCustomMarker(
          {
            lat: routeStop.latitude,
            lng: routeStop.longitude
          },
          this.getMarkerIcon(i, routeStop),
          routeStop.Address,
          markerLabel,
          this.locationMap,
          zIndex--
        )
        markerLabel = String.fromCharCode(markerLabel.charCodeAt(0) + 1)
      }
    },

    showMarkers(results) {
      this.clearMarkers()
      if (this.directionsDisplay) this.directionsDisplay.setMap(null)
      this.markerArray = []
      let markerLabel = 'A'
      let zIndex = 100

      for (let i = 0; i < results.length; i++) {
        const result = results[i]
        const { lat, lng } = result.position
        const locationCoordinate = {
          position: { lat, lng }
        }

        let infoWindow = null

        if (result.address) {
          const content = `<div id="iw-address">
          ${toCapitalCase(result.address)}
          </div>`

          // eslint-disable-next-line no-undef
          infoWindow = new google.maps.InfoWindow({ content })
        }

        let marker = null

        let markerIcon = this.customMarkerIcons.default
        if (result.address) {
          if (result.address.includes('Custom Start Location')) {
            markerIcon = this.customMarkerIcons.start
          } else if (result.address.includes('Custom End Location')) {
            markerIcon = this.customMarkerIcons.end
          }
        }
        // eslint-disable-next-line
        marker = new google.maps.Marker({
          ...locationCoordinate,
          map: this.locationMap,
          infowindow: infoWindow,
          label: { text: markerLabel, color: 'white' },
          icon: markerIcon,
          zIndex: zIndex--
        })
        markerLabel = String.fromCharCode(markerLabel.charCodeAt(0) + 1)

        if (infoWindow) {
          marker.addListener('mouseover', () => {
            infoWindow.open(this.locationMap, marker)
          })
        }
        marker.addListener('mouseout', () => {
          infoWindow.close()
        })

        this.markerArray.push(marker)
      }
    },

    clearMarkers() {
      for (let i = 0; i < this.markerArray.length; i++) {
        const result = this.markerArray[i]
        result.setMap(null)
      }
      for (let i = 0; i < this.customMarkerArray.length; i++) {
        const result = this.customMarkerArray[i]
        result.setMap(null)
      }
    },

    createCustomMarker(position, icon, title, label, map, zIndex) {
      const content = `<div id="iw-address">
          ${title}
          </div>`

      // eslint-disable-next-line no-undef
      const infoWindow = new google.maps.InfoWindow({ content })
      // eslint-disable-next-line
      const marker = new google.maps.Marker({
        position,
        map,
        infoWindow,
        zIndex,
        label: { text: label, color: 'white' },
        icon
      })

      if (infoWindow) {
        marker.addListener('mouseover', () => {
          infoWindow.open(this.locationMap, marker)
        })
      }
      marker.addListener('mouseout', () => {
        infoWindow.close()
      })
      this.customMarkerArray.push(marker)
    },

    getMarkerIcon(index, routeStop) {
      if (index === 0) {
        if (routeStop.propertyId) {
          return this.customMarkerIcons.default
        } else {
          return this.customMarkerIcons.start
        }
      } else if (!routeStop.propertyId) {
        return this.customMarkerIcons.end
      }
      return this.customMarkerIcons.default
    }
  }
}
</script>

<style lang="sass">
#iw-address
  font-weight: 500
  padding: 2rem
</style>

<style lang="sass" scoped>
.location-map
  position: relative
  width: 100%
</style>
